/**
 * usePipelinePersistence.ts - Pipeline Persistence Hook
 * 
 * CHANGE HISTORY:
 * - 251128_1238 danielbeer: Created hook for Cachr persistence and Backtrackr undo/redo
 * 
 * Features:
 * - Automatic persistence with Cachr
 * - Undo/redo with persistent Backtrackr
 * - Auto-save on changes
 * - Version tracking with Registr
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { MapperPipeline } from '../types/index';

// Optional dependencies
let Cachr: any = null;
let Backtrackr: any = null;
let Registr: any = null;

try {
  // @ts-ignore
  const dependr = require('@opcentral/dependr');
  Cachr = dependr.Cachr;
  Backtrackr = dependr.Backtrackr;
  Registr = dependr.Registr;
} catch (e) {
  console.warn('[usePipelinePersistence] Cachr/Backtrackr/Registr not available');
}

export interface UsePipelinePersistenceOptions {
  /** Pipeline ID for persistence */
  pipelineId?: string;
  
  /** Auto-save delay in ms */
  autoSaveDelay?: number;
  
  /** Enable version tracking */
  enableVersionTracking?: boolean;
  
  /** Callback when pipeline changes */
  onChange?: (pipeline: MapperPipeline) => void;
}

export interface UsePipelinePersistenceReturn {
  /** Current pipeline state */
  pipeline: MapperPipeline;
  
  /** Update pipeline */
  setPipeline: (pipeline: MapperPipeline | ((prev: MapperPipeline) => MapperPipeline)) => void;
  
  /** Undo last change */
  undo: () => void;
  
  /** Redo last undone change */
  redo: () => void;
  
  /** Can undo? */
  canUndo: boolean;
  
  /** Can redo? */
  canRedo: boolean;
  
  /** Save pipeline manually */
  save: () => Promise<void>;
  
  /** Load pipeline */
  load: (id: string) => Promise<MapperPipeline | null>;
  
  /** Clear pipeline */
  clear: () => void;
  
  /** Is saving? */
  isSaving: boolean;
  
  /** Last saved timestamp */
  lastSaved?: Date;
}

/**
 * Hook for pipeline persistence with Cachr and Backtrackr
 */
export function usePipelinePersistence(
  initialPipeline: MapperPipeline,
  options: UsePipelinePersistenceOptions = {}
): UsePipelinePersistenceReturn {
  const {
    pipelineId = 'default-pipeline',
    autoSaveDelay = 1000,
    enableVersionTracking = true,
    onChange
  } = options;

  const [pipeline, setPipelineState] = useState<MapperPipeline>(initialPipeline);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date>();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  const cachrRef = useRef<any>(null);
  const backtrackrRef = useRef<any>(null);
  const registrRef = useRef<any>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout>();

  // Initialize Cachr, Backtrackr, and Registr
  useEffect(() => {
    if (Cachr) {
      cachrRef.current = new Cachr({
        namespace: 'mappr-pipelines',
        persistent: true
      });
    }

    if (Backtrackr) {
      backtrackrRef.current = new Backtrackr({
        maxHistory: 50,
        persistent: true,
        persistenceKey: `mappr-history-${pipelineId}`
      });
    }

    if (Registr && enableVersionTracking) {
      registrRef.current = new Registr({
        namespace: 'mappr-versions',
        trackChanges: true
      });
    }

    // Load saved pipeline
    loadPipeline();
  }, [pipelineId, enableVersionTracking]);

  // Load pipeline from Cachr
  const loadPipeline = async () => {
    if (!cachrRef.current) return;

    try {
      const saved = await cachrRef.current.get(pipelineId);
      if (saved) {
        setPipelineState(saved);
        if (onChange) onChange(saved);
      }
    } catch (error) {
      console.error('[usePipelinePersistence] Error loading pipeline:', error);
    }
  };

  // Save pipeline to Cachr
  const savePipeline = useCallback(async () => {
    if (!cachrRef.current) return;

    setIsSaving(true);
    try {
      const pipelineToSave = {
        ...pipeline,
        id: pipelineId,
        updatedAt: new Date().toISOString()
      };

      await cachrRef.current.set(pipelineId, pipelineToSave);
      setLastSaved(new Date());

      // Track version with Registr
      if (registrRef.current && enableVersionTracking) {
        await registrRef.current.track({
          id: pipelineId,
          type: 'pipeline',
          version: pipelineToSave.version || '1.0.0',
          data: pipelineToSave,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('[usePipelinePersistence] Error saving pipeline:', error);
    } finally {
      setIsSaving(false);
    }
  }, [pipeline, pipelineId, enableVersionTracking]);

  // Auto-save on changes
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      savePipeline();
    }, autoSaveDelay);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [pipeline, autoSaveDelay, savePipeline]);

  // Update pipeline with Backtrackr
  const setPipeline = useCallback((update: MapperPipeline | ((prev: MapperPipeline) => MapperPipeline)) => {
    const newPipeline = typeof update === 'function' ? update(pipeline) : update;

    if (backtrackrRef.current) {
      backtrackrRef.current.execute({
        execute: () => {
          setPipelineState(newPipeline);
          if (onChange) onChange(newPipeline);
        },
        undo: () => {
          setPipelineState(pipeline);
          if (onChange) onChange(pipeline);
        },
        description: 'Update pipeline'
      });

      setCanUndo(backtrackrRef.current.canUndo());
      setCanRedo(backtrackrRef.current.canRedo());
    } else {
      setPipelineState(newPipeline);
      if (onChange) onChange(newPipeline);
    }
  }, [pipeline, onChange]);

  // Undo
  const undo = useCallback(() => {
    if (backtrackrRef.current && backtrackrRef.current.canUndo()) {
      backtrackrRef.current.undo();
      setCanUndo(backtrackrRef.current.canUndo());
      setCanRedo(backtrackrRef.current.canRedo());
    }
  }, []);

  // Redo
  const redo = useCallback(() => {
    if (backtrackrRef.current && backtrackrRef.current.canRedo()) {
      backtrackrRef.current.redo();
      setCanUndo(backtrackrRef.current.canUndo());
      setCanRedo(backtrackrRef.current.canRedo());
    }
  }, []);

  // Load pipeline by ID
  const load = useCallback(async (id: string): Promise<MapperPipeline | null> => {
    if (!cachrRef.current) return null;

    try {
      const loaded = await cachrRef.current.get(id);
      if (loaded) {
        setPipelineState(loaded);
        if (onChange) onChange(loaded);
      }
      return loaded;
    } catch (error) {
      console.error('[usePipelinePersistence] Error loading pipeline:', error);
      return null;
    }
  }, [onChange]);

  // Clear pipeline
  const clear = useCallback(() => {
    const emptyPipeline: MapperPipeline = {
      datasets: [],
      showFieldTypes: true,
      showSampleData: true,
      compact: false
    };
    setPipelineState(emptyPipeline);
    if (onChange) onChange(emptyPipeline);
  }, [onChange]);

  return {
    pipeline,
    setPipeline,
    undo,
    redo,
    canUndo,
    canRedo,
    save: savePipeline,
    load,
    clear,
    isSaving,
    lastSaved
  };
}
